/**
 * Composable для управления медиа устройствами (камеры и микрофоны)
 *
 * Отвечает за:
 * - Определение доступных устройств ввода/вывода
 * - Переключение между камерами (фронтальная/задняя)
 * - Переключение между микрофонами и динамиками
 * - Получение медиа потоков с выбранных устройств
 */

import { ref, type Ref } from 'vue';
import type {
  CameraInfo,
  CameraState,
  AudioDeviceInfo,
  AudioDeviceState,
  QualityLevel
} from '../types';
import { enumerateCameras, enumerateAudioInputDevices, enumerateAudioOutputDevices } from '../utils/cameraDetection';
import {
  stopTracksByKind,
  setTracksEnabled,
  copyAudioTrack,
  getVideoTrack,
  getAudioTrack,
  stopAllTracks
} from '../utils/mediaTracks';

export function useMediaDevices() {
  // ==================== СОСТОЯНИЕ ====================

  /** Локальный медиа поток (камера + микрофон) */
  const localStream: Ref<MediaStream | null> = ref(null);
  const remoteStream: Ref<MediaStream | null> = ref(null);

  /** Информация о доступных камерах */
  const cameraInfo: Ref<CameraState> = ref({
    availableCameras: [],
    currentCamera: null,
    hasFrontCamera: false,
    hasBackCamera: false,
    canSwitchCameras: false
  });

  /** Информация об аудио устройствах */
  const audioDeviceInfo: Ref<AudioDeviceState> = ref({
    availableInputDevices: [],
    availableOutputDevices: [],
    currentInputDevice: null,
    currentOutputDevice: null,
    hasMultipleInputs: false,
    hasMultipleOutputs: false
  });

  /** Флаги доступности медиа */
  const hasVideo: Ref<boolean> = ref(false);
  const hasAudio: Ref<boolean> = ref(false);

  // ==================== ОПРЕДЕЛЕНИЕ УСТРОЙСТВ ====================

  /**
   * Определяет доступные камеры на устройстве
   */
  async function detectAvailableCameras(): Promise<void> {
    try {
      console.log('📷 Поиск доступных камер...');

      const cameras = await enumerateCameras();

      if (cameras.length === 0) {
        console.warn('⚠️ Камеры не найдены');
        return;
      }

      // Обновляем состояние
      cameraInfo.value.availableCameras = cameras;
      cameraInfo.value.hasFrontCamera = cameras.some(cam => cam.type === 'front');
      cameraInfo.value.hasBackCamera = cameras.some(cam => cam.type === 'back');
      cameraInfo.value.canSwitchCameras = cameraInfo.value.hasFrontCamera && cameraInfo.value.hasBackCamera;

      console.log('✅ Найдено камер:', {
        total: cameras.length,
        front: cameraInfo.value.hasFrontCamera,
        back: cameraInfo.value.hasBackCamera,
        canSwitch: cameraInfo.value.canSwitchCameras
      });

      // Выбираем стартовую камеру
      selectInitialCamera();

    } catch (error) {
      console.error('❌ Ошибка определения камер:', error);
    }
  }

  /**
   * Определяет доступные аудио устройства (микрофоны и динамики)
   */
  async function detectAudioDevices(): Promise<void> {
    try {
      console.log('🎤 Поиск доступных аудио устройств...');

      const [inputs, outputs] = await Promise.all([
        enumerateAudioInputDevices(),
        enumerateAudioOutputDevices()
      ]);

      // Обновляем состояние
      audioDeviceInfo.value.availableInputDevices = inputs;
      audioDeviceInfo.value.availableOutputDevices = outputs;
      audioDeviceInfo.value.hasMultipleInputs = inputs.length > 1;
      audioDeviceInfo.value.hasMultipleOutputs = outputs.length > 1;

      // Выбираем устройства по умолчанию
      selectDefaultAudioDevices();

      console.log('✅ Найдено аудио устройств:', {
        inputs: inputs.length,
        outputs: outputs.length
      });

    } catch (error) {
      console.error('❌ Ошибка определения аудио устройств:', error);
    }
  }

  /**
   * Выбирает аудио устройства по умолчанию
   */
  function selectDefaultAudioDevices(): void {
    const { availableInputDevices, availableOutputDevices } = audioDeviceInfo.value;

    if (availableInputDevices.length > 0) {
      audioDeviceInfo.value.currentInputDevice = availableInputDevices[0];
    }

    if (availableOutputDevices.length > 0) {
      audioDeviceInfo.value.currentOutputDevice = availableOutputDevices[0];
    }
  }

  /**
   * Выбирает стартовую камеру при инициализации
   * Предпочитает фронтальную камеру для видео-звонков
   */
  function selectInitialCamera(): void {
    const { availableCameras, hasFrontCamera } = cameraInfo.value;

    if (availableCameras.length === 0) return;

    let selectedCamera: CameraInfo | null = null;

    // Предпочитаем фронтальную камеру
    if (hasFrontCamera) {
      selectedCamera = availableCameras.find(cam => cam.facingMode === 'user') || null;
    } else {
      selectedCamera = availableCameras[0];
    }

    if (selectedCamera) {
      cameraInfo.value.currentCamera = selectedCamera;
      console.log(`✅ Выбрана камера: ${selectedCamera.label} (${selectedCamera.type})`);
    }
  }

  // ==================== ПОЛУЧЕНИЕ МЕДИА ПОТОКОВ ====================

  /**
   * Получает медиа поток с конкретной камерой
   */
  async function getStreamWithCamera(
    cameraId?: string,
    quality?: { width: number; height: number; frameRate: number },
    audio: boolean = true
  ): Promise<MediaStream> {
    const targetCameraId = cameraId || cameraInfo.value.currentCamera?.deviceId;

    console.log('🔄 Получаем поток с камерой:', targetCameraId, quality, audio);

    const constraints: MediaStreamConstraints = {
      audio: audio ? {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      } : false,
      video: quality ? {
        width: { ideal: quality.width },
        height: { ideal: quality.height },
        frameRate: { ideal: quality.frameRate }
      } : cameraId ? {
        deviceId: { exact: cameraId }
      } : true
    };

    // Если указана конкретная камера
    if (targetCameraId && constraints.video && typeof constraints.video === 'object') {
      constraints.video.deviceId = { exact: targetCameraId };
    }

    console.log('🔄 Constraints:', constraints);

    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      localStream.value = stream;
      console.log(`✅ Получен поток с камеры: ${targetCameraId || 'default'}`);
      return stream;
    } catch (error) {
      console.error('❌ Ошибка получения потока с камеры:', error);

      // Fallback на камеру по умолчанию
      if (targetCameraId && constraints.video && typeof constraints.video === 'object') {
        console.log('🔄 Пробуем камеру по умолчанию...');
        delete constraints.video.deviceId;
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        localStream.value = stream;
        return stream;
      }

      throw error;
    }
  }

  /**
   * Получает медиа поток с конкретным микрофоном
   */
  async function getStreamWithAudioInput(
    deviceId: string,
    quality?: { width: number; height: number; frameRate: number }
  ): Promise<MediaStream> {
    const constraints: MediaStreamConstraints = {
      audio: {
        deviceId: { exact: deviceId },
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      },
      video: quality ? {
        width: { ideal: quality.width },
        height: { ideal: quality.height },
        frameRate: { ideal: quality.frameRate }
      } : true
    };

    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      localStream.value = stream;
      console.log(`✅ Получен поток с микрофона: ${deviceId}`);
      return stream;
    } catch (error) {
      console.error('❌ Ошибка получения потока с микрофона:', error);

      // Fallback на микрофон по умолчанию
      console.log('🔄 Пробуем микрофон по умолчанию...');
      if (constraints.audio && typeof constraints.audio === 'object') {
        delete constraints.audio.deviceId;
      }
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      localStream.value = stream;
      return stream;
    }
  }

  // ==================== ПЕРЕКЛЮЧЕНИЕ УСТРОЙСТВ ====================

  /**
   * Переключает камеру (фронтальная ↔ задняя)
   */
  async function switchCamera(
    replaceSenderTrack: (kind: 'audio' | 'video', track: MediaStreamTrack) => Promise<boolean>
  ): Promise<boolean> {
    if (!cameraInfo.value.canSwitchCameras) {
      console.warn('⚠️ Переключение камер недоступно');
      return false;
    }

    try {
      console.log('🔄 Переключение камеры...');

      const { currentCamera, availableCameras } = cameraInfo.value;
      const currentType = currentCamera?.type;

      // Находим противоположную камеру
      let targetCamera: CameraInfo | null = null;

      if (currentType === 'front') {
        targetCamera = availableCameras.find(cam => cam.type === 'back') || null;
      } else if (currentType === 'back') {
        targetCamera = availableCameras.find(cam => cam.type === 'front') || null;
      } else {
        targetCamera = availableCameras[0];
      }

      if (!targetCamera) {
        console.warn('⚠️ Целевая камера не найдена');
        return false;
      }

      // Сохраняем текущий аудио трек перед переключением
      const existingAudioTrack = getAudioTrack(localStream.value);
      const existingVideoTrack = getVideoTrack(localStream.value);

      // Останавливаем только видео трек
      stopTracksByKind(localStream.value, 'video');

      // Получаем новый поток только с видео для новой камеры (без аудио)
      const newStream = await getStreamWithCamera(targetCamera.deviceId, undefined, false);
      
      // Добавляем сохраненный аудио трек к новому потоку
      copyAudioTrack(localStream.value, newStream);

      cameraInfo.value.currentCamera = targetCamera;

      // Переподключаем видео трек к PeerConnection
      const videoTrack = newStream.getVideoTracks()[0];
      if (videoTrack) {
        await replaceSenderTrack('video', videoTrack);
      }

      // Если аудио трек был добавлен, переподключаем его тоже
      if (existingAudioTrack && existingAudioTrack.readyState === 'live') {
        const audioTrack = newStream.getAudioTracks().find(t => t.id === existingAudioTrack.id) || existingAudioTrack;
        if (audioTrack && audioTrack.readyState === 'live') {
          await replaceSenderTrack('audio', audioTrack);
        }
      }

      // Обновляем состояние потока
      localStream.value = newStream;

      console.log(`✅ Камера переключена на: ${targetCamera.label} (${targetCamera.type})`);
      return true;

    } catch (error) {
      console.error('❌ Ошибка переключения камеры:', error);
      return false;
    }
  }

  /**
   * Переключает на конкретную камеру
   */
  async function switchToSpecificCamera(
    camera: CameraInfo,
    replaceSenderTrack: (kind: 'audio' | 'video', track: MediaStreamTrack) => Promise<boolean>
  ): Promise<boolean> {
    try {
      console.log(`🔄 Переключение на камеру: ${camera.label} ${camera.deviceId}`);

      // Сохраняем текущий аудио трек перед переключением
      const existingAudioTrack = getAudioTrack(localStream.value);

      // Останавливаем только видео трек
      stopTracksByKind(localStream.value, 'video');

      // Получаем новый поток только с видео для новой камеры (без аудио)
      const newStream = await getStreamWithCamera(camera.deviceId, undefined, false);
      
      // Добавляем сохраненный аудио трек к новому потоку
      copyAudioTrack(localStream.value, newStream);

      cameraInfo.value.currentCamera = camera;

      // Переподключаем видео трек к PeerConnection
      const videoTrack = getVideoTrack(newStream);
      if (videoTrack) {
        await replaceSenderTrack('video', videoTrack);
      }

      // Если аудио трек был добавлен, переподключаем его тоже
      const audioTrack = getAudioTrack(newStream);
      if (audioTrack && existingAudioTrack && audioTrack.id === existingAudioTrack.id) {
        await replaceSenderTrack('audio', audioTrack);
      }

      // Обновляем состояние потока
      localStream.value = newStream;

      console.log(`✅ Переключено на камеру: ${camera.label} (${camera.type})`);
      return true;

    } catch (error) {
      console.error('❌ Ошибка переключения на камеру:', error);
      return false;
    }
  }

  /**
   * Переключает микрофон
   */
  async function switchAudioInput(
    deviceId: string,
    replaceSenderTrack: (kind: 'audio' | 'video', track: MediaStreamTrack) => Promise<boolean>
  ): Promise<boolean> {
    try {
      console.log(`🎤 Переключение микрофона: ${deviceId}`);

      const device = audioDeviceInfo.value.availableInputDevices.find(d => d.deviceId === deviceId);
      if (!device) {
        console.warn('⚠️ Микрофон не найден');
        return false;
      }

      // Останавливаем аудио треки
      stopTracksByKind(localStream.value, 'audio');

      // Получаем новый поток
      await getStreamWithAudioInput(deviceId);
      audioDeviceInfo.value.currentInputDevice = device;

      // Переподключаем трек к PeerConnection
      const audioTrack = getAudioTrack(localStream.value);
      if (audioTrack) {
        await replaceSenderTrack('audio', audioTrack);
      }

      console.log(`✅ Микрофон переключен: ${device.label}`);
      return true;

    } catch (error) {
      console.error('❌ Ошибка переключения микрофона:', error);
      return false;
    }
  }

  /**
   * Переключает динамики (устройство вывода)
   */
  async function switchAudioOutput(deviceId: string): Promise<boolean> {
    try {
      console.log(`🔊 Переключение динамиков: ${deviceId}`);

      const device = audioDeviceInfo.value.availableOutputDevices.find(d => d.deviceId === deviceId);
      if (!device) {
        console.warn('⚠️ Устройство вывода не найдено');
        return false;
      }

      audioDeviceInfo.value.currentOutputDevice = device;
      console.log(`✅ Динамики переключены: ${device.label}`);
      return true;

    } catch (error) {
      console.error('❌ Ошибка переключения динамиков:', error);
      return false;
    }
  }

  // ==================== УПРАВЛЕНИЕ ТРЕКАМИ ====================

  /**
   * Включает/выключает аудио трек
   */
  function toggleAudioTrack(enabled: boolean): void {
    setTracksEnabled(localStream.value, 'audio', enabled);
  }

  /**
   * Включает/выключает видео трек
   */
  function toggleVideoTrack(enabled: boolean): void {
    setTracksEnabled(localStream.value, 'video', enabled);
  }

  /**
   * Очистка ресурсов
   */
  function cleanup(): void {
    if (localStream.value) {
      stopAllTracks(localStream.value);
      localStream.value = null;
    }
  }

  // ==================== ВОЗВРАЩАЕМЫЙ API ====================

  return {
    // Состояние
    localStream,
    remoteStream,
    cameraInfo,
    audioDeviceInfo,
    hasVideo,
    hasAudio,

    // Определение устройств
    detectAvailableCameras,
    detectAudioDevices,

    // Получение потоков
    getStreamWithCamera,
    getStreamWithAudioInput,

    // Переключение устройств
    switchCamera,
    switchToSpecificCamera,
    switchAudioInput,
    switchAudioOutput,

    // Управление треками
    toggleAudioTrack,
    toggleVideoTrack,

    // Очистка
    cleanup
  };
}
